import { Opt, PostCode } from 'app/tools/general.types';
import { getActions, getReducers, keyByName } from 'app/tools/utils';
import { combineReducers } from 'redux';
import {
  ApiInfoRtn,
  NoContent,
  StatusResp,
} from './api-core/api-core.types';
import apiSliceFactory from './api-core/utility-factories/api-slice-factory';
import { LoginRtn, RegistrationRtn } from './auth/api-auth.types';
import {
  Address,
  CalendarData,
  Company,
  CompanyBankDetails,
  CompanyDetailsPt1,
  NotificationCount,
  NotificationData,
  RepairCapability,
} from './company/api-company.types';
import {
  AssignedProduct,
  AssignedProductLite,
  AssignProductImageRtn,
  AssignProductRtn,
  RepairAssignedProduct,
} from './product-assignment/api-product-assignment.types';
import { ProductDefect } from './product-defect/api-product-defect.types';
import {
  Product,
  ProductBrand,
  ProductCategory,
  ProductCategoryByBrand,
  ProductFromSearchV1,
} from './product/api-product.types';
import { RatingRtn } from './rating/api-rating.types';
import { WarrantyCoveredRtn } from './warranty-covered/api-warranty-covered.types';
import {
  CompetitionTerms,
  Referral,
  ReferralData,
} from './referral/api-referral.types';
import {
  BankStatus,
  EventEntity,
  PaymentMethodResp,
  QuoteSummery,
  RepairCase,
  RepairerSearchRtn,
  RepairLocation,
  RepairPersonalDetail,
  TransactionQuote,
} from './repairer/api-repairer.types';
import { RepairStats } from './stats/api-stats.types';
import {
  IdentityPlus,
  DeliveryLocations,
  UserProfile,
  HistoryData,
  RepairerInfo,
} from './user/api-user.types';
import { GoogleGeolocation } from './utils-3rd-party/api-google-geolocation.types';

const apiSlices = [
  // Auth API
  apiSliceFactory<RegistrationRtn>()('registration'),
  apiSliceFactory<LoginRtn>()('login'),
  apiSliceFactory<ApiInfoRtn>()('logout'),
  apiSliceFactory<ApiInfoRtn>()('changePassword'),
  apiSliceFactory<ApiInfoRtn>()('resetPassword'),
  apiSliceFactory<ApiInfoRtn>()('confirmResetPassword'),
  apiSliceFactory<LoginRtn>()('externalSignIn'),

  // User Profile API
  apiSliceFactory<IdentityPlus>()('identity'),
  apiSliceFactory<UserProfile>()('userProfile'),
  apiSliceFactory<StatusResp>()('setUserProfile'),
  apiSliceFactory<StatusResp>()('setUserProfileImg'),
  apiSliceFactory<StatusResp>()('deleteUser'),
  apiSliceFactory<StatusResp>()('setPaymentMethod'),
  apiSliceFactory<PaymentMethodResp>()('getPaymentMethod'),
  apiSliceFactory<NoContent>()('deletePaymentMethod'),
  apiSliceFactory<DeliveryLocations[]>()('getAddresses'),
  apiSliceFactory<StatusResp>()('setAddress'),
  apiSliceFactory<HistoryData[]>()('getTransactionHistory'),
  apiSliceFactory<HistoryData[]>()('getConsumerTransaction'),
  apiSliceFactory<NoContent>()('setRepairersActivity'),
  apiSliceFactory<RepairerInfo>()('getRepairerInfo'),
  apiSliceFactory()('deleteChat'),

  // Company Api
  apiSliceFactory<CompanyDetailsPt1>()('companyDetailsPt1'),
  apiSliceFactory<Company>()('companyDetails'),
  apiSliceFactory<Company>()('currentCompanyDetails'),
  apiSliceFactory<Company>()('updateCurrentCompanyDetails'),
  apiSliceFactory<CompanyBankDetails>()('currentCompanyBankDetails'),
  apiSliceFactory<CompanyBankDetails>()('createCompanyBankDetails'),
  apiSliceFactory()('deleteCompanyBankDetails'),
  apiSliceFactory<CompanyBankDetails>()('updateCompanyBankDetails'),
  apiSliceFactory<RepairCapability[]>()('currentRepairCapabilities'),
  apiSliceFactory<RepairCapability[]>()('setRepairCapabilities'),
  apiSliceFactory<StatusResp>()('setRepairPersonelAddress'),
  apiSliceFactory<Address>()('getRepairPersonelAddress'),
  apiSliceFactory<StatusResp>()('updateRepairPersonelAddress'),
  apiSliceFactory<RepairLocation[]>()('repairLocations'),
  apiSliceFactory<StatusResp>()('setRepairPersonalDetail'),
  apiSliceFactory<StatusResp>()('updateRepairPersonalDetail'),
  apiSliceFactory<RepairPersonalDetail[]>()(
    'getRepairPersonalDetail'
  ),
  apiSliceFactory<StatusResp>()('setRepairAccountType'),
  apiSliceFactory<RepairLocation[]>()('repairLocations'),
  apiSliceFactory<RepairLocation>()('createRepairLocation'),
  apiSliceFactory<NoContent>()('deleteRepairLocation'),
  apiSliceFactory<CalendarData[]>()('calendarList'),
  apiSliceFactory<CalendarData[]>()('setCalendarDetail'),
  apiSliceFactory<StatusResp>()('updatePushNotification'),
  apiSliceFactory<NotificationData>()('setNotificationListData'),
  apiSliceFactory<NotificationCount>()('getNotificationCount'),
  apiSliceFactory<StatusResp>()('readNotification'),

  apiSliceFactory<NotificationCount>()(
    'getConsumerNotificationCount'
  ),
  apiSliceFactory<StatusResp>()('readConsumerNotification'),
  apiSliceFactory<StatusResp>()('readRepairerNotification'),

  // Product API
  apiSliceFactory<ProductFromSearchV1[]>()('searchProducts'),
  apiSliceFactory<ProductFromSearchV1>()('getProduct'),
  apiSliceFactory<Product>()('createCustomProduct'),
  apiSliceFactory<ProductCategory[]>()('productCategories'),
  apiSliceFactory<ProductBrand[]>()('productBrands'),
  apiSliceFactory<ProductCategoryByBrand[]>()(
    'productCategoriesByBrand'
  ),

  // Product Assignments API
  apiSliceFactory<AssignedProductLite[]>()('assignedProductsLite'),
  apiSliceFactory<RepairAssignedProduct[]>()(
    'repairAssignedProducts'
  ),
  apiSliceFactory<AssignedProduct>()('assignedProduct'),
  apiSliceFactory<AssignedProduct>()('assignProduct'),
  apiSliceFactory<AssignProductRtn>()('assignProductV1'),
  apiSliceFactory<AssignProductRtn>()('assignProductV1Blank'),
  apiSliceFactory<AssignProductImageRtn>()('assignProductReceiptImg'),
  apiSliceFactory<AssignProductImageRtn>()('assignProductCustomImg'),
  apiSliceFactory<NoContent>()('deleteAssignedProduct'),

  // Product Defect API
  apiSliceFactory<ProductDefect[]>()('productDefects'),
  apiSliceFactory<ProductDefect>()('createProductDefect'),

  // Repairer API
  apiSliceFactory<RepairerSearchRtn>()('repairers'),
  apiSliceFactory<BankStatus>()('getRepairerBankStatus'),

  // Repair Case API
  apiSliceFactory<EventEntity>()('createRepairEvent'),
  apiSliceFactory<EventEntity>()('createRepairImageEvent'),
  apiSliceFactory<RepairCase[]>()('getRepairCases'),
  apiSliceFactory<RepairCase>()('repairCase'),
  apiSliceFactory<RepairCase>()('initiateRepairChat'),
  apiSliceFactory<Opt<number>>()('assignmentOpenRepair'),
  apiSliceFactory<QuoteSummery>()('quoteSummery'),
  apiSliceFactory<StatusResp>()('setQuoteSummery'),
  apiSliceFactory<StatusResp>()('setWarrantyClaim'),
  apiSliceFactory<StatusResp>()('setWarrantyClaimPayment'),
  apiSliceFactory<StatusResp>()('setQuotePayment'),
  apiSliceFactory<StatusResp>()('setUpdateStatus'),
  apiSliceFactory<TransactionQuote[]>()('getRepairerHistory'),
  apiSliceFactory<TransactionQuote[]>()('getRepairerTransaction'),
  apiSliceFactory<TransactionQuote[]>()('getRepaircasePaymentDetail'),

  // Rating API
  apiSliceFactory<RatingRtn>()('createRating'),

  // Warranty Covered
  apiSliceFactory<WarrantyCoveredRtn>()(
    'createRepairCaseWarrantyCovered'
  ),

  // Referral API
  apiSliceFactory<Referral>()('createReferral'),
  apiSliceFactory<ReferralData[]>()('getReferralDetails'),
  apiSliceFactory<CompetitionTerms[]>()('getCompetitionTerms'),

  // Reporting API
  apiSliceFactory<RepairStats>()('repairStats'),

  // EXTERNAL -------------------------------------------------------
  // PostcodeIO API
  apiSliceFactory<PostCode | null>()('postcodeLookup'),
  // Google Geolocation API
  apiSliceFactory<GoogleGeolocation>()('googleGeolocation'),
];

export const apiRedux = keyByName(apiSlices);

export const apiActions = getActions(apiRedux);
export const apiReducer = combineReducers(getReducers(apiRedux));
